/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useLoanContext } from '../../contexts/LoanContext';
import { useCallback, useState } from 'react';
import { isObjEmptyOrNull, isStrEmptyOrNull, getErrorFromEndpoint } from '../UtilityFunctions';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

const useEscrowAnalysisCalculator = () => {
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const { get } = useAbortController();
  const [escrowAnalysisCalculation, setEscrowAnalysisCalculation] =
    useState<UnknownEscrowAnalysisCalculationResp>(null);
  const [escrowCalcErrors, setEscrowCalcErrors] = useState('');
  const [escrowCalcLoading, setEscrowCalcLoading] = useState(false);

  const escrowAnalysisCalculatorAPI = useCallback(
    async (params: EscrowAnalysisCalcReq): Promise<UnknownEscrowAnalysisCalculationResp> => {
      if (isStrEmptyOrNull(token) || isStrEmptyOrNull(loanNumber) || isObjEmptyOrNull(params)) return null;
      const urlPath = process.env.REACT_APP_ESCROW_ANALYSIS_CALCULATOR.replace('?', loanNumber);
      const request = { url: urlPath, data: params, method: 'post' };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const config = get();

      const res = await axiosCall(request, config, headers)
        .then((res) => {
          if (!isObjEmptyOrNull(res)) {
            const err = getErrorFromEndpoint(res);
            if (isStrEmptyOrNull(err)) {
              setEscrowAnalysisCalculation(res);
              return res;
            } else {
              console.log(err);
              setEscrowCalcErrors(err);
              return null;
            }
          }
          return null;
        })
        .catch((e: Error) => {
          console.log('Error using escrowAnalysisCalculatorAPI in the useEscrowAnalysisCalculator hook: ', e);
          setEscrowCalcErrors(e.message);
          return null;
        });
      setEscrowCalcLoading(false);
      return res;
    },
    [get, token, loanNumber],
  );

  const getEscrowAnalysisCalculation = useCallback(
    async (params: EscrowAnalysisCalcReq) => {
      setEscrowAnalysisCalculation(null);
      if (!isStrEmptyOrNull(loanNumber) && !isObjEmptyOrNull(params)) {
        setEscrowCalcLoading(true);
        const res = await escrowAnalysisCalculatorAPI(params);
        return res;
      }
      return null;
    },
    [escrowAnalysisCalculatorAPI, loanNumber],
  );

  return { escrowAnalysisCalculation, escrowCalcLoading, escrowCalcErrors, getEscrowAnalysisCalculation };
};

export default useEscrowAnalysisCalculator;
